import { Loader2 } from 'lucide-react';
import { useState } from 'react';

import { Button } from '@apps/web/components/ui/button';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@apps/web/components/ui/dialog';
import { Textarea } from '@apps/web/components/ui/textarea';
import { getApiClient } from '@apps/web/lib/api_client';
import { toast } from '@apps/web/lib/app_notifications';
import { ApiError } from '@libraries/typescript/api_client';
import { type components } from '@libraries/typescript/api_client/generated_api_types';

type ShiftRenotifyPreview = components['schemas']['ShiftRenotifyPreview'];

const MESSAGE_BODY_MAX_LENGTH = 320;

const FIELD_LABELS: Record<string, string> = {
    shift_start_time: 'Start time',
    shift_end_time: 'End time',
    patient_city: 'Location',
    role_required: 'Required role',
    skills_required: 'Required skills',
    notes_for_caregiver: 'Caregiver notes',
};

type RenotifyConfirmModalProps = {
    readonly isOpen: boolean;
    readonly onClose: () => void;
    readonly preview: ShiftRenotifyPreview;
    readonly shiftId: string;
    readonly route:
        | { readonly kind: 'organization'; readonly organizationId: string }
        | { readonly kind: 'admin' };
};

export function RenotifyConfirmModal({
    isOpen,
    onClose,
    preview,
    shiftId,
    route,
}: RenotifyConfirmModalProps) {
    const [messageBody, setMessageBody] = useState(
        preview.default_message_body,
    );
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [staleError, setStaleError] = useState<string | null>(null);

    const recipientCount = preview.recipients.length;
    const trimmedLength = messageBody.trim().length;
    // Match the API's validation: it strips before length-checking, so
    // counting the untrimmed length here would block bodies the API
    // would accept (e.g. 318 chars + 3 trailing spaces).
    const isOverLimit = trimmedLength > MESSAGE_BODY_MAX_LENGTH;
    // Once we've returned 409, the token is stale — repeated clicks would
    // just re-hit the same error. Force the user to close + re-open with a
    // fresh preview before they can try again.
    const canSend =
        trimmedLength > 0 && !isOverLimit && !isSubmitting && !staleError;

    async function handleSend() {
        setIsSubmitting(true);
        setStaleError(null);
        try {
            if (route.kind === 'organization') {
                await getApiClient().post({
                    url: '/organizations/{organization_id}/shifts/{shift_id}/renotify',
                    pathParams: {
                        organization_id: route.organizationId,
                        shift_id: shiftId,
                    },
                    body: {
                        preview_token: preview.preview_token,
                        message_body: messageBody,
                    },
                });
            } else {
                await getApiClient().post({
                    url: '/admin/shifts/{shift_id}/renotify',
                    pathParams: { shift_id: shiftId },
                    body: {
                        preview_token: preview.preview_token,
                        message_body: messageBody,
                    },
                });
            }
            toast.success(
                `Notified ${recipientCount} caregiver${recipientCount === 1 ? '' : 's'}`,
                { persistNotification: true, notificationType: 'shift_action' },
            );
            onClose();
        } catch (error) {
            if (error instanceof ApiError && error.status === 409) {
                setStaleError(
                    'This shift was edited again. Close this dialog, edit again, and confirm the new preview.',
                );
            } else {
                console.error('Failed to send renotify SMS', error);
                toast.error('Failed to send renotify SMS', {
                    persistNotification: true,
                    notificationType: 'shift_action',
                });
            }
        } finally {
            setIsSubmitting(false);
        }
    }

    function handleOpenChange(open: boolean) {
        if (!open && !isSubmitting) {
            onClose();
        }
    }

    return (
        <Dialog open={isOpen} onOpenChange={handleOpenChange}>
            <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-[600px]">
                <DialogHeader>
                    <DialogTitle>
                        Notify {recipientCount} caregiver
                        {recipientCount === 1 ? '' : 's'} about this change?
                    </DialogTitle>
                    <DialogDescription>
                        Caregivers already contacted on the active outreach will
                        receive the message below. Edit the text if you want to
                        phrase the change differently.
                    </DialogDescription>
                </DialogHeader>
                <div className="space-y-4">
                    <section className="space-y-2">
                        <h3 className="text-sm font-medium">What changed</h3>
                        <ul className="space-y-1 text-sm">
                            {preview.diff_summary.map((entry) => (
                                <li
                                    key={entry.field_name}
                                    className="flex items-baseline gap-2"
                                >
                                    <span className="font-medium">
                                        {FIELD_LABELS[entry.field_name] ??
                                            entry.field_name}
                                        :
                                    </span>
                                    <span className="text-muted-foreground">
                                        {entry.before_display}
                                    </span>
                                    <span className="text-muted-foreground">
                                        →
                                    </span>
                                    <span>{entry.after_display}</span>
                                </li>
                            ))}
                        </ul>
                    </section>
                    <section className="space-y-2">
                        <div className="flex items-center justify-between">
                            <h3 className="text-sm font-medium">Message</h3>
                            <span
                                className={
                                    isOverLimit
                                        ? 'text-destructive text-xs'
                                        : 'text-muted-foreground text-xs'
                                }
                            >
                                {trimmedLength}/{MESSAGE_BODY_MAX_LENGTH}
                            </span>
                        </div>
                        <Textarea
                            value={messageBody}
                            onChange={(event) =>
                                setMessageBody(event.target.value)
                            }
                            rows={5}
                            disabled={isSubmitting}
                        />
                        <p className="text-muted-foreground text-xs">
                            If a caregiver speaks another language, we’ll
                            translate this message before sending.
                        </p>
                    </section>
                    {staleError ? (
                        <p role="alert" className="text-destructive text-sm">
                            {staleError}
                        </p>
                    ) : null}
                </div>
                <DialogFooter>
                    <Button
                        type="button"
                        variant="outline"
                        onClick={onClose}
                        disabled={isSubmitting}
                    >
                        Don’t send
                    </Button>
                    <Button
                        type="button"
                        onClick={handleSend}
                        disabled={!canSend}
                    >
                        {isSubmitting ? (
                            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        ) : null}
                        Send to {recipientCount} caregiver
                        {recipientCount === 1 ? '' : 's'}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
